import {ProductSearchRefinementValue} from 'coreStore/data/productSearchResult/interface';
import RefinementModel from './RefinementModel';
import SearchModel from './SearchModel';
import AbstractSearchProvider from './providers/AbstractSearchProvider';

export const ALL_KEYWORD = 'ALL';

/**
 * Represents RefinementModel
 */
class RefinementValueModel {
	private refinementValue: ProductSearchRefinementValue;
	private refinementModel: RefinementModel;
	private values: Array<RefinementValueModel>;

	/**
	 * @constructor
	 * @param {ProductSearchRefinementValue} refinementValue - refinement value data
	 * @param {RefinementModel} refinementModel - refinementModel
	 */
	constructor(refinementModel: RefinementModel, refinementValue: ProductSearchRefinementValue) {
		this.refinementModel = refinementModel;
		this.refinementValue = refinementValue;
	}

	/**
	 * Returns a unique key
	 * @returns {string}
	 */
	getKey(): string {
		return `ref-filter-value-core-${this.getAttributeId()}${this.getLabel().replace(' ', '_')}`;
	}

	/**
	 * Returns the description
	 * @returns {string}
	 */
	getDescription(): string {
		return this.refinementValue?.description || '';
	}

	/**
	 * Returns the presentation id
	 * @returns {string}
	 */
	getPresentationId(): string {
		return this.refinementValue?.presentationId || '';
	}

	/**
	 * Returns the attribute ID
	 * @returns {string}
	 */
	getAttributeId(): string {
		return this.getRefinementModel().getAttributeId();
	}

	/**
	 * Returns the value
	 * @returns {string}
	 */
	getValue(): string {
		return this.refinementValue?.value || null;
	}

	/**
	 * Returns the attribute values
	 * @returns {RefinementValueModel[]}
	 */
	getValues(): Array<RefinementValueModel>  {
		if (!this.values) {
			this.values = this.refinementValue?.values
				?.map((v) => new RefinementValueModel(this.getRefinementModel(), v)) || [];
		}

		return this.values;
	}

	/**
	 * Returns the label
	 * @returns {string}
	 */
	getLabel(): string {
		return this.refinementValue?.label || '';
	}

	/**
	 * Returns order of the current refinement
	 * @returns {number}
	 */
	getOrder(): number {
		return this.refinementValue?.order || 0;
	}

	/**
	 * Returns true if it is a "select all" filter
	 * @returns {boolean}
	 */
	isFilterAll(): boolean {
		return this.getDescription() === ALL_KEYWORD;
	}

	/**
	 * Returns the hit count of the current value
	 * @returns {number}
	 */
	getHitCount(): number {
		return this.refinementValue.hitCount || 0;
	}

	/**
	 * Returns the refinement model
	 * @returns {RefinementModel}
	 */
	getRefinementModel(): RefinementModel {
		return this.refinementModel;
	}

	/**
	 * Returns the search manager instance
	 * @deprecated Please use getSearchProvider
	 * @returns {AbstractSearchProvider}
	 */
	getSearchMgrInstance(): AbstractSearchProvider {
		return this.getSearchProvider();
	}

	/**
	 * Returns the search provider instance
	 * @returns {AbstractSearchProvider}
	 */
	getSearchProvider(): AbstractSearchProvider {
		return this.getSearchModel().getSearchProvider();
	}

	/**
	 * Returns the search model instance
	 * @returns {SearchModel}
	 */
	getSearchModel(): SearchModel {
		return this.refinementModel.getSearchModel();
	}

	/**
	 * Toggles the current refinement value
	 * @returns {void}
	 */
	toggle(switchValue: boolean|null = null): void {
		this.getRefinementModel()
			.getSearchModel()
			.getSearchProvider()
			.toggleRefinementValueModel(this, switchValue);
	}

	/**
	 * Returns true if the refinement value is active
	 * @returns {boolean}
	 */
	isActive(): boolean {
		const selectedRefinements = this.getRefinementModel()
			.getSearchModel()
			.getSearchProvider()
			.getUserSelectedRefinements();
		const attributeId = this.getRefinementModel().getAttributeId();

		return !!(
			selectedRefinements &&
			selectedRefinements[attributeId] &&
			selectedRefinements[attributeId].indexOf(this.getValue()) !== -1
		);
	}
}

export default RefinementValueModel;
